/**
 * 認証コンテキスト
 * #2133 設計 §3: 権限の判定は ID トークンの Custom Claims（role・companyId・
 * studioId）だけで行う。判定できないときは見せない（fail-closed）。
 * ログイン中も users/{uid} を購読し、削除・ロール変更が入ったらトークンを
 * 取り直し（getIdTokenResult(true)）、権限が外れていればサインアウトする。
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type { User } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import {
  deriveRoleFlags,
  extractRoleClaims,
  NO_ROLE_CLAIMS,
  type RoleClaims,
  type RoleFlags,
} from '@/lib/roleFlags';

const loadFirebaseAuth = () => import('@/lib/firebase');

interface ClaimsResolution {
  ok: boolean;
  claims: RoleClaims;
}

export interface AuthContextType {
  /** Firebase Auth ユーザー */
  user: User | null;
  /** 認証状態（onAuthStateChanged）がまだ確定していないか */
  loading: boolean;
  /**
   * user が確定した後、その user の Custom Claims・パスワード変更要否の取得が
   * まだ解決していないか。loading とは独立して管理する
   * （darshana Issue #2227 の profileLoading と同じ考え方）。
   */
  claimsLoading: boolean;
  /** Firebase の設定（環境変数）が欠落しており認証機能が利用できないか */
  authUnavailable: boolean;
  /** 認証済みかどうか */
  isAuthenticated: boolean;
  /** 初回パスワード変更が未完了か（tempUsers.isInitialPasswordChanged === false） */
  needsPasswordChange: boolean;
  /** Custom Claims から導出した権限フラグ */
  roleFlags: RoleFlags;
  /** メール/パスワードでサインイン */
  signIn: (email: string, password: string) => Promise<void>;
  /** サインアウト */
  signOut: () => Promise<void>;
  /** 初回パスワード変更を完了し、サインアウトする（再ログインを促す） */
  completePasswordChange: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Custom Claims を取り直す */
  refreshClaims: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedUid, setResolvedUid] = useState<string | null>(null);
  const [claims, setClaims] = useState<RoleClaims | null>(null);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [authUnavailable, setAuthUnavailable] = useState(false);

  // サインイン・サインアウトが連続して起きた場合に、古い非同期解決が後から
  // 新しい状態を上書きしないようにする世代番号（darshana Issue #1624 と同じ対応）。
  const generationRef = useRef(0);

  const claimsLoading = user !== null && resolvedUid !== user.uid;

  const resolveClaims = useCallback(async (target: User, forceRefresh: boolean): Promise<ClaimsResolution> => {
    try {
      const tokenResult = await target.getIdTokenResult(forceRefresh);
      return { ok: true, claims: extractRoleClaims(tokenResult.claims) };
    } catch (error) {
      logger.error('Failed to fetch ID token claims', error);
      return { ok: false, claims: NO_ROLE_CLAIMS };
    }
  }, []);

  // tempUsers/{uid} の isInitialPasswordChanged を確認する。tempUsers を
  // 使わない（既に users へ昇格済みの）ユーザーは null が返り、
  // パスワード変更不要として扱う。取得自体に失敗した場合は fail-open
  // （ユーザーを締め出さない）にする。これは「権限」ではなく
  // 「オンボーディング状態」の判定であり、権限判定の fail-closed 方針とは
  // 別の軸であるため意図的に区別する。
  const resolvePasswordChangeStatus = useCallback(async (uid: string): Promise<boolean> => {
    try {
      const { fetchTempUserPasswordChangeStatus } = await loadFirebaseAuth();
      const isChanged = await fetchTempUserPasswordChangeStatus(uid);
      return isChanged === false;
    } catch (error) {
      logger.error('Failed to fetch tempUsers password-change status', error);
      return false;
    }
  }, []);

  const resolveForUser = useCallback(async (target: User) => {
    const myGeneration = ++generationRef.current;
    const [claimsResult, needsChange] = await Promise.all([
      resolveClaims(target, false),
      resolvePasswordChangeStatus(target.uid),
    ]);

    if (generationRef.current !== myGeneration) {
      // より新しい世代の解決が既に始まっている
      return;
    }

    setClaims(claimsResult.claims);
    setNeedsPasswordChange(needsChange);
    setResolvedUid(target.uid);
  }, [resolveClaims, resolvePasswordChangeStatus]);

  const clearResolvedState = useCallback(() => {
    generationRef.current += 1;
    setClaims(null);
    setNeedsPasswordChange(false);
    setResolvedUid(null);
  }, []);

  const signOutInternal = useCallback(async () => {
    try {
      const { signOut: firebaseSignOut } = await loadFirebaseAuth();
      await firebaseSignOut();
    } catch (error) {
      logger.error('Failed to sign out', error);
    } finally {
      setUser(null);
      clearResolvedState();
    }
  }, [clearResolvedState]);

  // 認証状態の監視
  useEffect(() => {
    let active = true;
    let unsubscribe = () => {};

    const startAuthWatch = async () => {
      let firebaseAuthModule: Awaited<ReturnType<typeof loadFirebaseAuth>>;
      try {
        firebaseAuthModule = await loadFirebaseAuth();
      } catch (error) {
        logger.error('Failed to load firebase auth module', error);
        if (active) {
          setAuthUnavailable(true);
          setLoading(false);
        }
        return;
      }
      if (!active) return;

      unsubscribe = firebaseAuthModule.onAuthStateChange(
        async (firebaseUser) => {
          if (!active) return;

          setUser(firebaseUser);

          if (firebaseUser) {
            await resolveForUser(firebaseUser);
          } else {
            clearResolvedState();
          }

          if (active) {
            setLoading(false);
          }
        },
        () => {
          if (!active) return;
          setAuthUnavailable(true);
          setLoading(false);
        }
      );
    };

    void startAuthWatch();

    return () => {
      active = false;
      unsubscribe();
    };
  }, [resolveForUser, clearResolvedState]);

  // users/{uid} の購読: 削除・ロール変更の検知（設計§3）。
  // 初回スナップショットでは何もしない（resolveForUser 側で既に取得済みの
  // claims と重複するだけのため）。2回目以降の変化でのみトークンを取り直し、
  // 失敗または fail-closed（role === null）になった場合・isDeleted===true の
  // 場合はサインアウトする。
  useEffect(() => {
    if (!user) {
      return;
    }

    let active = true;
    let isFirstSnapshot = true;
    let unsubscribeDoc = () => {};
    const uid = user.uid;
    const target = user;

    const setup = async () => {
      const { subscribeToOwnUserDoc } = await loadFirebaseAuth();
      if (!active) return;

      unsubscribeDoc = subscribeToOwnUserDoc(
        uid,
        (docData) => {
          if (!active) return;
          if (isFirstSnapshot) {
            isFirstSnapshot = false;
            // 既に削除済みの人がログイン・リロードしても、手元の ID トークン
            // (最大1時間有効)の Claims だけで通さない。初回でも削除済みなら締め出す。
            if (docData?.isDeleted === true) {
              void signOutInternal();
            }
            return;
          }

          void (async () => {
            const result = await resolveClaims(target, true);
            if (!result.ok || result.claims.role === null || docData?.isDeleted === true) {
              await signOutInternal();
            } else {
              setClaims(result.claims);
            }
          })();
        },
        (error) => {
          logger.error('users/{uid} subscription failed, signing out', error);
          void signOutInternal();
        }
      );
    };

    void setup();

    return () => {
      active = false;
      unsubscribeDoc();
    };
  }, [user, resolveClaims, signOutInternal]);

  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const { signInWithEmail } = await loadFirebaseAuth();
      const credential = await signInWithEmail(email, password);
      setUser(credential.user);
      await resolveForUser(credential.user);
    } finally {
      setLoading(false);
    }
  }, [resolveForUser]);

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      await signOutInternal();
    } finally {
      setLoading(false);
    }
  }, [signOutInternal]);

  const completePasswordChange = useCallback(async (currentPassword: string, newPassword: string) => {
    if (!user) {
      throw new Error('completePasswordChange called without a signed-in user');
    }
    const { changePassword, markInitialPasswordChanged } = await loadFirebaseAuth();
    await changePassword(user, currentPassword, newPassword);
    await markInitialPasswordChanged(user.uid);
    // #2133 設計: claims の反映（onTempUserPasswordChanged -> onUserCreated）には
    // サーバー側の非同期処理が必要なため、ここでは待たずにサインアウトして
    // 再ログインを促す（待機・ポーリングより単純で確実）。
    await signOutInternal();
  }, [user, signOutInternal]);

  const refreshClaims = useCallback(async () => {
    if (!user) return;
    await resolveForUser(user);
  }, [user, resolveForUser]);

  const roleFlags = useMemo(() => deriveRoleFlags(claims), [claims]);

  const value = useMemo<AuthContextType>(() => ({
    user,
    loading,
    claimsLoading,
    authUnavailable,
    isAuthenticated: user !== null,
    needsPasswordChange,
    roleFlags,
    signIn,
    signOut,
    completePasswordChange,
    refreshClaims,
  }), [
    user,
    loading,
    claimsLoading,
    authUnavailable,
    needsPasswordChange,
    roleFlags,
    signIn,
    signOut,
    completePasswordChange,
    refreshClaims,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
